// src/pages/Users/Galleries/components/AlbumPreviewModal.jsx

import React, { useEffect, useMemo } from "react";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaImage,
  FaTimes,
} from "react-icons/fa";

import {
  FALLBACK_ALBUM_IMG,
  getPreviewPhotos,
  stripHtml,
} from "../utils/galleriesUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "album-preview-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-700 sm:text-base",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-orange-600",
  "hover:prose-a:underline",
  "dark:prose-a:text-orange-300",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-orange-500",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK_ALBUM_IMG;
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function AlbumPreviewModal({
  isOpen,
  album,
  onClose,
}) {
  const navigate = useNavigate();

  const photos = useMemo(() => {
    return getPreviewPhotos(album, 12);
  }, [album]);

  const totalPhotos = Array.isArray(album?.photos)
    ? album.photos.length
    : 0;

  const safeDescription = useMemo(() => {
    const rawDescription = album?.description || "";
    const plainDescription = stripHtml(rawDescription);

    if (!plainDescription) return "";

    return sanitizeHtml(rawDescription);
  }, [album?.description]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [isOpen, onClose]);

  if (!isOpen || !album) return null;

  const openAlbum = () => {
    onClose?.();

    navigate(`/galerie-photos/album/${album.id}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/75 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Prévisualisation ${
        album?.titre || "album"
      }`}
      onClick={onClose}
    >
      <article
        className="mx-auto flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête */}

        <header className="shrink-0 border-b border-slate-200 p-5 dark:border-slate-800 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-600 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-300">
                <FaImage />
                Prévisualisation
              </span>

              <h2 className="mt-4 line-clamp-2 text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
                {album?.titre || "Album"}
              </h2>

              <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                {totalPhotos} photo
                {totalPhotos > 1 ? "s" : ""} —{" "}
                {photos.length} affichée
                {photos.length > 1 ? "s" : ""} en aperçu
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-lg ring-1 ring-slate-200 transition hover:text-slate-950 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/30 active:scale-95 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:text-white"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        {/* Contenu scrollable */}

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {/* Description HTML enrichie */}

          {safeDescription ? (
            <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
              <div
                className={RICH_TEXT_CLASSES}
                style={{ tabSize: 4 }}
                dangerouslySetInnerHTML={{
                  __html: safeDescription,
                }}
              />
            </div>
          ) : null}

          {/* Photos */}

          {photos.length === 0 ? (
            <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
              <FaImage className="mx-auto text-4xl text-slate-300" />

              <p className="mt-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                Aucune photo à prévisualiser.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((photo, index) => (
                <button
                  key={
                    photo.id ||
                    `${album.id}-${index}`
                  }
                  type="button"
                  onClick={openAlbum}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/30 dark:border-slate-800 dark:bg-slate-950"
                  aria-label={`Voir toutes les photos depuis la photo ${
                    index + 1
                  }`}
                >
                  <div className="aspect-[16/10] w-full">
                    <img
                      src={
                        photo.absolute_url ||
                        FALLBACK_ALBUM_IMG
                      }
                      alt={stripHtml(
                        photo.description ||
                          `Photo ${index + 1}`,
                      )}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                      loading="lazy"
                      onError={onImgError}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}

        <footer className="shrink-0 border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Pour éviter les problèmes d’affichage sur mobile,
              la prévisualisation est volontairement limitée.
            </p>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 shadow-sm transition focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/30 active:scale-95 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                Fermer
              </button>

              <button
                type="button"
                onClick={openAlbum}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 text-sm font-bold text-white shadow-lg shadow-orange-600/20 transition hover:bg-orange-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/30 active:scale-95"
              >
                Voir toutes les photos
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
}